import type {
  MedalCategory,
  MedalMaterial,
  MedalProduct,
  MedalProductDimension,
  MedalRibbonType,
  MedalRibbonStyle,
  MedalRibbonColor,
  MedalSetting,
  MedalQuote,
  MedalQuoteCustomItem,
  MedalQuoteExtraItem,
  CreateMedalCategoryRequest,
  CreateMedalMaterialRequest,
  CreateMedalProductRequest,
  DimensionRequest,
  RibbonTypeRequest,
  RibbonStyleRequest,
  RibbonColorRequest,
} from '../types/medals';
import { apiRequest } from './api-client';

const MEDALS = '/backoffice/medals';

// ===== Categories =====

export async function fetchCategories(token: string, includeInactive = false): Promise<MedalCategory[]> {
  return apiRequest(`${MEDALS}/categories?includeInactive=${includeInactive}`, token);
}

export async function createCategory(data: CreateMedalCategoryRequest, token: string): Promise<MedalCategory> {
  return apiRequest(`${MEDALS}/categories`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCategory(id: string, data: Partial<CreateMedalCategoryRequest>, token: string): Promise<MedalCategory> {
  return apiRequest(`${MEDALS}/categories/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCategory(id: string, token: string): Promise<void> {
  await apiRequest(`${MEDALS}/categories/${id}`, token, { method: 'DELETE' });
}

export async function toggleCategoryStatus(id: string, token: string): Promise<MedalCategory> {
  return apiRequest(`${MEDALS}/categories/${id}/status`, token, { method: 'PATCH' });
}

// ===== Materials =====

export async function fetchMaterials(token: string, params?: { search?: string; includeInactive?: boolean; includeDeleted?: boolean }): Promise<MedalMaterial[]> {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.includeInactive) query.set('includeInactive', 'true');
  if (params?.includeDeleted) query.set('includeDeleted', 'true');
  return apiRequest(`${MEDALS}/materials?${query}`, token);
}

export async function createMaterial(data: CreateMedalMaterialRequest, token: string): Promise<MedalMaterial> {
  return apiRequest(`${MEDALS}/materials`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateMaterial(id: string, data: Partial<CreateMedalMaterialRequest>, token: string): Promise<MedalMaterial> {
  return apiRequest(`${MEDALS}/materials/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteMaterial(id: string, token: string): Promise<void> {
  await apiRequest(`${MEDALS}/materials/${id}`, token, { method: 'DELETE' });
}

export async function restoreMaterial(id: string, token: string): Promise<MedalMaterial> {
  return apiRequest(`${MEDALS}/materials/${id}/restore`, token, { method: 'POST' });
}

export async function toggleMaterialStatus(id: string, token: string): Promise<MedalMaterial> {
  return apiRequest(`${MEDALS}/materials/${id}/status`, token, { method: 'PATCH' });
}

// ===== Products =====

export async function fetchProducts(token: string, params?: { search?: string; categoryId?: string; includeInactive?: boolean }): Promise<MedalProduct[]> {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.categoryId) query.set('categoryId', params.categoryId);
  if (params?.includeInactive) query.set('includeInactive', 'true');
  return apiRequest(`${MEDALS}/products?${query}`, token);
}

export async function fetchProduct(id: string, token: string): Promise<MedalProduct> {
  return apiRequest(`${MEDALS}/products/${id}`, token);
}

export async function fetchProductDimensions(productId: string, token: string): Promise<MedalProductDimension[]> {
  return apiRequest(`${MEDALS}/products/${productId}/dimensions`, token);
}

export async function createProduct(data: CreateMedalProductRequest, token: string): Promise<MedalProduct> {
  return apiRequest(`${MEDALS}/products`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateProduct(id: string, data: Partial<CreateMedalProductRequest>, token: string): Promise<MedalProduct> {
  return apiRequest(`${MEDALS}/products/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteProduct(id: string, token: string): Promise<void> {
  await apiRequest(`${MEDALS}/products/${id}`, token, { method: 'DELETE' });
}

export async function toggleProductStatus(id: string, token: string): Promise<MedalProduct> {
  return apiRequest(`${MEDALS}/products/${id}/status`, token, { method: 'PATCH' });
}

// Reorder products
export async function reorderProducts(token: string, items: { id: string; displayOrder: number }[]): Promise<void> {
  await apiRequest(`${MEDALS}/products/reorder`, token, {
    method: 'PUT',
    body: JSON.stringify(items),
  });
}

// Dimensions
export async function addDimension(productId: string, data: DimensionRequest, token: string): Promise<MedalProductDimension> {
  return apiRequest(`${MEDALS}/products/${productId}/dimensions`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDimension(dimensionId: string, data: DimensionRequest, token: string): Promise<MedalProductDimension> {
  return apiRequest(`${MEDALS}/products/dimensions/${dimensionId}`, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteDimension(dimensionId: string, token: string): Promise<void> {
  await apiRequest(`${MEDALS}/products/dimensions/${dimensionId}`, token, { method: 'DELETE' });
}

// ===== Ribbons =====

export async function fetchRibbonTypes(token: string, includeInactive = false): Promise<MedalRibbonType[]> {
  return apiRequest(`${MEDALS}/ribbons/types?includeInactive=${includeInactive}`, token);
}

export async function createRibbonType(data: RibbonTypeRequest, token: string): Promise<MedalRibbonType> {
  return apiRequest(`${MEDALS}/ribbons/types`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRibbonType(id: string, data: RibbonTypeRequest, token: string): Promise<MedalRibbonType> {
  return apiRequest(`${MEDALS}/ribbons/types/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteRibbonType(id: string, token: string): Promise<void> {
  await apiRequest(`${MEDALS}/ribbons/types/${id}`, token, { method: 'DELETE' });
}

export async function fetchRibbonStyles(token: string, includeInactive = false): Promise<MedalRibbonStyle[]> {
  return apiRequest(`${MEDALS}/ribbons/styles?includeInactive=${includeInactive}`, token);
}

export async function createRibbonStyle(data: RibbonStyleRequest, token: string): Promise<MedalRibbonStyle> {
  return apiRequest(`${MEDALS}/ribbons/styles`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRibbonStyle(id: string, data: RibbonStyleRequest, token: string): Promise<MedalRibbonStyle> {
  return apiRequest(`${MEDALS}/ribbons/styles/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteRibbonStyle(id: string, token: string): Promise<void> {
  await apiRequest(`${MEDALS}/ribbons/styles/${id}`, token, { method: 'DELETE' });
}

export async function fetchRibbonColors(token: string, includeInactive = false): Promise<MedalRibbonColor[]> {
  return apiRequest(`${MEDALS}/ribbons/colors?includeInactive=${includeInactive}`, token);
}

export async function createRibbonColor(data: RibbonColorRequest, token: string): Promise<MedalRibbonColor> {
  return apiRequest(`${MEDALS}/ribbons/colors`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRibbonColor(id: string, data: RibbonColorRequest, token: string): Promise<MedalRibbonColor> {
  return apiRequest(`${MEDALS}/ribbons/colors/${id}`, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteRibbonColor(id: string, token: string): Promise<void> {
  await apiRequest(`${MEDALS}/ribbons/colors/${id}`, token, { method: 'DELETE' });
}

// ===== Settings =====

export async function fetchSettings(token: string): Promise<MedalSetting[]> {
  return apiRequest(`${MEDALS}/settings`, token);
}

export async function fetchSettingsMap(token: string): Promise<Record<string, string>> {
  return apiRequest(`${MEDALS}/settings/map`, token);
}

export async function updateSetting(key: string, value: string, description: string | undefined, token: string): Promise<MedalSetting> {
  return apiRequest(`${MEDALS}/settings/${key}`, token, {
    method: 'PUT',
    body: JSON.stringify({ value, description }),
  });
}

export async function updateSettings(settings: Record<string, string>, token: string): Promise<void> {
  await apiRequest(`${MEDALS}/settings`, token, {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

// ===== Quotes =====

export async function fetchQuotes(token: string, params?: { search?: string; status?: string; startDate?: string; endDate?: string }): Promise<MedalQuote[]> {
  const query = new URLSearchParams();
  if (params?.search) query.set('search', params.search);
  if (params?.status) query.set('status', params.status);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  return apiRequest(`${MEDALS}/quotes?${query}`, token);
}

export async function fetchQuote(id: string, token: string): Promise<MedalQuote> {
  return apiRequest(`${MEDALS}/quotes/${id}`, token);
}

export interface CreateMedalQuoteRequest {
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerCompany?: string;
  customerDocument?: string;
  customerSegment?: string;
  notes?: string;
}

export async function createQuote(data: CreateMedalQuoteRequest, token: string): Promise<MedalQuote> {
  return apiRequest(`${MEDALS}/quotes`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQuoteStatus(id: string, status: string, token: string): Promise<MedalQuote> {
  return apiRequest(`${MEDALS}/quotes/${id}/status`, token, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export async function fetchPendingCustomItems(token: string): Promise<MedalQuoteCustomItem[]> {
  return apiRequest(`${MEDALS}/quotes/pending-custom-items`, token);
}

export interface ConfirmCustomItemPricePayload {
  materialId?: string;
  bodyCount?: number;
  printSides?: number;
  confirmedPrice?: number;
}

export async function confirmCustomItemPrice(itemId: string, payload: ConfirmCustomItemPricePayload, token: string): Promise<MedalQuoteCustomItem> {
  return apiRequest(`${MEDALS}/quotes/custom-items/${itemId}/confirm-price`, token, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function calculateCustomItemPrice(
  materialId: string,
  widthCm: number,
  heightCm: number,
  thicknessMm: number,
  bodyCount: number,
  printSides: number,
  quantity: number,
  token: string
): Promise<{ unitPrice: number; subtotal: number }> {
  return apiRequest(`${MEDALS}/quotes/calculate-custom-item`, token, {
    method: 'POST',
    body: JSON.stringify({ materialId, widthCm, heightCm, thicknessMm, bodyCount, printSides, quantity }),
  });
}

// ===== Quote Item Management =====

export async function updateQuoteItem(itemId: string, data: { quantity: number }, token: string): Promise<any> {
  return apiRequest(`${MEDALS}/quotes/items/${itemId}`, token, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteQuoteItem(itemId: string, token: string): Promise<void> {
  await apiRequest(`${MEDALS}/quotes/items/${itemId}`, token, { method: 'DELETE' });
}

export async function addQuoteItem(quoteId: string, data: any, token: string): Promise<any> {
  return apiRequest(`${MEDALS}/quotes/${quoteId}/items`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ===== Quote Extra Items =====

export async function fetchQuoteExtraItems(quoteId: string, token: string): Promise<MedalQuoteExtraItem[]> {
  return apiRequest(`${MEDALS}/quotes/${quoteId}/extra-items`, token);
}

export async function addExtraItem(quoteId: string, description: string, value: number, token: string): Promise<MedalQuoteExtraItem> {
  return apiRequest(`${MEDALS}/quotes/${quoteId}/extra-items`, token, {
    method: 'POST',
    body: JSON.stringify({ description, value }),
  });
}

export async function deleteExtraItem(itemId: string, token: string): Promise<void> {
  await apiRequest(`${MEDALS}/quotes/extra-items/${itemId}`, token, { method: 'DELETE' });
}

export async function updateExtraItems(
  quoteId: string,
  items: Array<{ description: string; value: number }>,
  token: string
): Promise<void> {
  await apiRequest(`${MEDALS}/quotes/${quoteId}/extra-items`, token, {
    method: 'PUT',
    body: JSON.stringify(items),
  });
}

// ===== Quote Discount =====

export async function updateQuoteDiscount(quoteId: string, discount: string, token: string): Promise<MedalQuote> {
  return apiRequest(`${MEDALS}/quotes/${quoteId}/discount`, token, {
    method: 'PATCH',
    body: JSON.stringify({ discount }),
  });
}

// ===== Quote View Tracking =====

export async function markMedalQuoteAsViewed(quoteId: string, token: string): Promise<MedalQuote> {
  return apiRequest(`${MEDALS}/quotes/${quoteId}/viewed`, token, {
    method: 'POST',
  });
}
